import { useEffect, useRef } from 'preact/hooks'
import { useAthosContext } from '../fetchers/provider'
import { useOption } from '../hooks/useOption'
import { scrollPlpGridIntoView } from '../helpers/scrollToGrid'
import Icon from '@/components/snippets/icon/icon'
import clsx from 'clsx'

const ELLIPSIS = 'ellipsis'
const START_BLOCK_SIZE = 3
const MIDDLE_WINDOW_SIZE = 3
const END_BLOCK_SIZE = 3

const PAGE_BUTTON_CLASS = 'btn btn-sm md:btn-md btn-icon btn-inline'

export function getPageNumbers(activePage, totalPages) {
  if (totalPages <= START_BLOCK_SIZE + 1) {
    return Array.from({ length: totalPages }, (_, i) => i + 1)
  }

  const pages = [1]
  const startThreshold = START_BLOCK_SIZE
  const endThreshold = totalPages - END_BLOCK_SIZE + 1

  if (activePage < startThreshold) {
    for (let i = 2; i <= Math.min(START_BLOCK_SIZE, totalPages); i++) pages.push(i)
    if (totalPages > START_BLOCK_SIZE) {
      pages.push(ELLIPSIS)
      pages.push(totalPages)
    }
    return pages
  }

  if (activePage >= endThreshold) {
    const left = Math.max(2, totalPages - END_BLOCK_SIZE + 1)
    pages.push(ELLIPSIS)
    for (let i = left; i <= totalPages; i++) pages.push(i)
    return pages
  }

  const left = Math.max(2, activePage - (MIDDLE_WINDOW_SIZE - 1))
  const right = Math.min(totalPages - 1, left + MIDDLE_WINDOW_SIZE - 1)
  pages.push(ELLIPSIS)
  for (let i = left; i <= right; i++) pages.push(i)
  pages.push(ELLIPSIS)
  pages.push(totalPages)
  return pages
}

export function Pagination() {
  const { results, refine, isLoading } = useAthosContext()
  const pendingScroll = useRef(false)

  const activePage = useOption('page', {
    defaultValue: 1,
    syncWithURL: true
  })

  const totalPages = results?.pagination?.totalPages ?? 1

  useEffect(() => {
    if (!pendingScroll.current || isLoading) return
    pendingScroll.current = false
    scrollPlpGridIntoView()
    const retry = window.setTimeout(scrollPlpGridIntoView, 100)
    return () => window.clearTimeout(retry)
  }, [isLoading, activePage])

  if (totalPages <= 1) return null

  const pageNumbers = getPageNumbers(activePage, totalPages)

  const goToPage = (page) => {
    if (page === activePage || page < 1 || page > totalPages) return
    pendingScroll.current = true
    refine('page', page)
    scrollPlpGridIntoView()
  }

  return (
    <nav className="flex w-full items-center justify-center" aria-label="Pagination">
      <div className="flex items-center gap-2 py-8 md:py-12">
        <button
          type="button"
          className={clsx(
            PAGE_BUTTON_CLASS,
            'btn-outline-black'
          )}
          onClick={() => goToPage(activePage - 1)}
          disabled={activePage <= 1}
          aria-label="Previous page"
        >
          <Icon id="chevron-left" className="size-4" />
        </button>
        {pageNumbers.map((page, index) =>
          page === ELLIPSIS ? (
            <span key={`ellipsis-${index}`} className="flex size-10 items-center justify-center paragraph-sm">
              ...
            </span>
          ) : (
            <button
              key={page}
              type="button"
              className={clsx(
                PAGE_BUTTON_CLASS,
                page === activePage ? 'btn-solid-black' : 'border border-neutrals-base'
              )}
              onClick={() => goToPage(page)}
              aria-label={`Page ${page}`}
              aria-current={page === activePage ? 'page' : undefined}
            >
              <span>{page}</span>
            </button>
          )
        )}
        <button
          type="button"
          className={clsx(
            PAGE_BUTTON_CLASS,
            'btn-outline-black'
          )}
          onClick={() => goToPage(activePage + 1)}
          disabled={activePage >= totalPages}
          aria-label="Next page"
        >
          <Icon id="chevron-right" className="size-4" />
        </button>
      </div>
    </nav>
  )
}
